/**
 * RateLimitToastBridge
 *
 * The axios interceptor (utils/apiClient.js) lives outside React, so it can't call
 * useNotification() directly. It dispatches a `window` CustomEvent 'api:rate-limited'
 * instead; this component — mounted INSIDE NotificationProvider — turns that event
 * into a single warning toast.
 *
 * Mount race: the first 429 can fire during app bootstrap BEFORE this component
 * mounts, and a CustomEvent with no listener is lost. The interceptor therefore also
 * buffers the latest event on window.__lastRateLimitEvent; on mount we replay it once
 * (if recent) and clear it, so a first-load 429 storm still produces a toast.
 */

import { useEffect } from 'react';
import { useNotification } from '../../contexts/NotificationContext';

const MESSAGE = "You're sending requests too quickly and have been temporarily rate limited. Please slow down and try again in a moment.";

function RateLimitToastBridge() {
  const notify = useNotification();

  useEffect(() => {
    const show = () => {
      try { notify.warning(MESSAGE); } catch (e) { /* notify unavailable */ }
    };

    const onRateLimited = () => show();
    window.addEventListener('api:rate-limited', onRateLimited);

    // Replay a buffered event that fired before we mounted (only if very recent, so we
    // don't surface a stale throttle from a previous session/tab).
    try {
      const buffered = window.__lastRateLimitEvent;
      if (buffered && typeof buffered.at === 'number' && Date.now() - buffered.at < 10000) {
        show();
      }
      window.__lastRateLimitEvent = null;
    } catch (e) { /* no window */ }

    return () => window.removeEventListener('api:rate-limited', onRateLimited);
    // notify identity is stable enough; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default RateLimitToastBridge;
